// Shopify theme checkout entry points. Liquid decides which entry point is
// shown to a customer; the Worker independently enforces the test allowlists.
(function () {
  const errorMessage = 'Unable to start purchase. Please sign in and try again.';
  const pendingWithoutWebLocks = new Set();

  function newRequestId() {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0'));
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}`
      + `-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}`
      + `-${hex.slice(10).join('')}`;
  }

  async function withCheckoutLock(storageKey, operation) {
    if (navigator.locks && typeof navigator.locks.request === 'function') {
      return navigator.locks.request(storageKey, operation);
    }
    // Older Shopify/in-app browsers may not expose the Web Locks API. Keep an
    // in-page mutex; cross-tab duplicates still carry the same persisted UUID
    // and are deduplicated by the Worker/VPS transaction boundary.
    if (pendingWithoutWebLocks.has(storageKey)) return;
    pendingWithoutWebLocks.add(storageKey);
    try {
      return await operation();
    } finally {
      pendingWithoutWebLocks.delete(storageKey);
    }
  }

  // 「以港币支付（支持微信）」是本轮灰度新增的自选项：只在灰度弹窗里渲染
  // （主题按 customer.tags contains 'vps_checkout_test' 决定），勾了才发偏好。
  // 记住选择：勾过就写 localStorage，下次默认还是港币；取消勾选即改回美元。
  const HKD_PREF_KEY = 'plotcraft-checkout-hkd';
  function storedHkd() {
    try { return localStorage.getItem(HKD_PREF_KEY) === '1'; } catch (_) { return false; }
  }
  // ⚠️ 必须定义在 IIFE 顶层：放到 durableCheckout 里的话，监听器是"购买开始时"
  // 才注册的，而那时 DOMContentLoaded 早就过了 —— 回填永远不会跑。
  let hkdRestored = false;
  function restoreHkdChoice() {
    const box = (typeof document !== 'undefined' && document.getElementById)
      ? document.getElementById('pcPayHkd') : null;
    if (!box) return false;
    try { box.checked = storedHkd(); } catch (_) {}
    hkdRestored = true;
    return true;
  }
  // 勾选框由主题片段渲染，出现时机不定（可能在 DOMContentLoaded 之后），所以除了
  // 监听事件，取值前也补一次回填；否则一个默认未勾的框会把记忆读成"用美元"。
  if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('DOMContentLoaded', restoreHkdChoice);
  }
  function preferHkd() {
    const box = (typeof document !== 'undefined' && document.getElementById)
      ? document.getElementById('pcPayHkd') : null;
    if (!box) return storedHkd();
    if (!hkdRestored) restoreHkdChoice();
    return box.checked === true;
  }
  function rememberHkd(value) {
    try { localStorage.setItem(HKD_PREF_KEY, value ? '1' : '0'); } catch (_) {}
  }

  async function durableCheckout(target, buyerScope, payload, probeBase) {
    if (!/^[1-9]\d{0,29}$/.test(String(buyerScope || ''))) {
      alert(errorMessage);
      return;
    }
    const storageKey = `plotcraft-checkout-v2:${buyerScope}:${target}`;
    try {
      return await withCheckoutLock(storageKey, async () => {
        let requestId = localStorage.getItem(storageKey);
        if (!requestId) {
          requestId = newRequestId();
          // Persist before network I/O. If storage is unavailable, do not charge.
          localStorage.setItem(storageKey, requestId);
        }
        // 主题带来的国家：Shopify 自己按本地化判定的 `window.Shopify.country`。
        // 零新增资源、不需要大陆可达的直连主机名，客户端可改但**经济上中性**
        // （金额由服务端按基价 × 当日汇率算，换币种不会少收钱）。
        function shopifyCountry() {
          const value = (globalThis.Shopify && globalThis.Shopify.country) || '';
          return String(value).toUpperCase();
        }

        async function countryToken() {
          // 直连探测只在给了 probeBase 时才有意义：经 App Proxy 的那一跳，
          // Cloudflare 看到的是 Shopify 转发节点，算出来的国家不是用户的。
          const base = new URL(probeBase || '');
          if (base.protocol !== 'https:') throw new Error('REGION_UNAVAILABLE');
          const response = await fetch(new URL('/vps-country-probe', base).href,
            {credentials: 'omit', cache: 'no-store', redirect: 'error'});
          const data = response.ok ? await response.json() : null;
          if (!data || typeof data.token !== 'string' || !data.token) {
            throw new Error('REGION_UNAVAILABLE');
          }
          return data.token;
        }
        async function send(attempt) {
          const controller = new AbortController();
          const timer = setTimeout(() => controller.abort(), 30000);
          try {
            // 取不到地区就不下单：宁可让用户重试，也不静默按错误币种收费。
            // 两级来源：① 主题带来的 `Shopify.country`（默认路径）；
            //           ② 直连探测签发的 token（只有在给了 probeBase 时才试）。
            // 两者都没有 → 拒绝创建，交回给客户端重试。
            const country = shopifyCountry();
            const wantHkd = preferHkd();
            // 只在真实页面（有勾选框）里持久化：沙箱/无 DOM 场景不该产生副作用。
            if (typeof document !== 'undefined' && document.getElementById
                && document.getElementById('pcPayHkd')) rememberHkd(wantHkd);
            const body = {...payload, request_id: attempt, country,
              ...(wantHkd ? {currency: 'hkd'} : {})};
            if (probeBase) {
              body.country_token = await countryToken();
            } else if (!country) {
              throw new Error('REGION_UNAVAILABLE');
            }
            const response = await fetch('/apps/episode/vps-series-checkout', {
              method: 'POST',
              credentials: 'same-origin',
              cache: 'no-store',
              signal: controller.signal,
              headers: {'Content-Type': 'application/json'},
              body: JSON.stringify(body),
            });
            return {response, data: await response.json()};
          } finally {
            clearTimeout(timer);
          }
        }
        let {response, data} = await send(requestId);
        if (response.status === 409 && data.code === 'CHECKOUT_EXPIRED') {
          // The server has definitively closed the unpaid Session, so a new
          // request ID cannot double-charge the old attempt.
          localStorage.removeItem(storageKey);
          requestId = newRequestId();
          localStorage.setItem(storageKey, requestId);
          ({response, data} = await send(requestId));
        }
        if (response.status === 409 && data.code === 'PURCHASE_ALREADY_PAID') {
          alert(payload.purchase_kind === 'membership'
            ? 'Your membership is already active.'
            : 'This series is already unlocked. You can watch it now.');
          return;
        }
        if (!response.ok || !data.checkout_url) {
          throw new Error(data.code || 'CHECKOUT_UNAVAILABLE');
        }
        const url = new URL(data.checkout_url);
        if (url.origin !== 'https://checkout.stripe.com'
            || url.username || url.password) {
          throw new Error('INVALID_CHECKOUT_URL');
        }
        window.location.assign(url.href);
      });
    } catch (_) {
      // Preserve the request ID after unknown outcomes. A retry reconciles the
      // same attempt and never falls back to a second payment path.
      alert(errorMessage);
    }
  }

  window.pcBuy = async function (dramaId, buyerScope, probeBase) {
    if (!/^gid:\/\/shopify\/Metaobject\/[1-9]\d*$/.test(dramaId || '')) {
      alert(errorMessage);
      return;
    }
    return durableCheckout(dramaId, buyerScope, {drama_gid: dramaId}, probeBase);
  };

  window.pcBuyMembership = async function (membershipPlanId, buyerScope, probeBase) {
    if (!/^[1-9]\d{0,9}$/.test(String(membershipPlanId || ''))) {
      alert(errorMessage);
      return;
    }
    return durableCheckout(`membership:${membershipPlanId}`, buyerScope, {
      purchase_kind: 'membership',
      membership_plan_id: String(membershipPlanId),
    }, probeBase);
  };

  // Exact production Payment Link flow retained for customers outside the
  // test cohort. It is deliberately a separate function so test failures can
  // never fall through into a live charge.
  window.pcBuyLegacy = async function (dramaId) {
    if (window.pcBuyLegacy.pending) return;
    window.pcBuyLegacy.pending = true;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch(
        '/apps/episode/shared-series-payment?drama_gid=' + encodeURIComponent(dramaId),
        {credentials: 'same-origin', cache: 'no-store', signal: controller.signal},
      );
      const data = await response.json();
      if (!response.ok || !data.url) throw new Error('Purchase unavailable');
      const url = new URL(data.url);
      if (url.origin !== 'https://buy.stripe.com'
          || !/^\/[a-zA-Z0-9]+$/.test(url.pathname)
          || url.username || url.password
          || !/^pc1_[0-9a-f]{48}$/.test(url.searchParams.get('client_reference_id') || '')) {
        throw new Error('Invalid purchase');
      }
      window.location.assign(url.href);
    } catch (_) {
      alert(errorMessage);
    } finally {
      clearTimeout(timer);
      window.pcBuyLegacy.pending = false;
    }
  };
})();
